import React from 'react';
import { useNavigate } from 'react-router-dom';
import Landing from '../components/Landing';
import DynamicSection from '../components/dynamic/DynamicSection';
import DynamicCard from '../components/dynamic/DynamicCard';
import DynamicButton from '../components/dynamic/DynamicButton';
import DynamicTag from '../components/dynamic/DynamicTag';
import {
  FaClock,
  FaCalendar,
  FaUser,
  FaArrowRight,
  FaCode,
  FaServer,
  FaPaintBrush,
  FaCloud,
  FaBrain,
  FaComments,
  FaCheck,
  FaStar,
  FaQuoteLeft,
  FaRocket,
} from 'react-icons/fa';
import { blogAPI, servicesAPI, testimonialsAPI } from '../services/api';
import { BlogPost, Service, Testimonial } from '../types';

const iconMap: { [key: string]: any } = {
  FaCode,
  FaServer,
  FaPaintBrush,
  FaCloud,
  FaBrain,
  FaComments,
};

// Helper function to convert country code to flag emoji
const getCountryFlag = (countryCode: string | undefined): string => {
  if (!countryCode) return '';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [featuredBlogs, setFeaturedBlogs] = React.useState<BlogPost[]>([]);
  const [services, setServices] = React.useState<Service[]>([]);
  const [testimonials, setTestimonials] = React.useState<Testimonial[]>([]);

  React.useEffect(() => {
    const loadData = async () => {
      try {
        const [blogsRes, servicesRes, testimonialsRes] = await Promise.all([
          blogAPI.getAll(),
          servicesAPI.getAll(),
          testimonialsAPI.getAll(),
        ]);
        
        // Get featured blogs (where featured: true)
        const featured = blogsRes.data.filter(blog => blog.featured).slice(0, 1);
        setFeaturedBlogs(featured);
        
        // Get web development related services (filter by keywords)
        const webDevServices = servicesRes.data.filter(service => {
          const titleLower = service.title.toLowerCase();
          const descLower = service.description.toLowerCase();
          return titleLower.includes('web') || titleLower.includes('frontend') || 
                 titleLower.includes('backend') || titleLower.includes('full stack') ||
                 descLower.includes('web') || descLower.includes('react') || 
                 descLower.includes('node') || descLower.includes('javascript');
        }).slice(0, 3);
        setServices(webDevServices);
        
        // Get best testimonials related to web development (filter by keywords and high rating)
        const webDevTestimonials = testimonialsRes.data.filter(testimonial => {
          const contentLower = testimonial.content.toLowerCase();
          const projectLower = testimonial.project?.toLowerCase() || '';
          return (contentLower.includes('web') || contentLower.includes('website') || 
                  contentLower.includes('frontend') || contentLower.includes('backend') ||
                  contentLower.includes('react') || contentLower.includes('node') ||
                  projectLower.includes('web') || projectLower.includes('website')) &&
                 testimonial.rating >= 4;
        }).slice(0, 2);
        setTestimonials(webDevTestimonials);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  // Generate placeholder image function
  const getPlaceholderImage = (post: BlogPost) => {
    const category = post.category || 'Blog Post';
    const gradientId = `grad-${post.id}`;
    const svg = `
      <svg width="800" height="400" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="${gradientId}" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#4F46E5;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#7C3AED;stop-opacity:1" />
          </linearGradient>
        </defs>
        <rect width="800" height="400" fill="url(#${gradientId})"/>
        <text x="50%" y="45%" font-family="Arial, sans-serif" font-size="32" font-weight="bold" 
              fill="white" text-anchor="middle" dominant-baseline="middle">${category}</text>
        <text x="50%" y="55%" font-family="Arial, sans-serif" font-size="18" 
              fill="rgba(255,255,255,0.9)" text-anchor="middle" dominant-baseline="middle">Blog Article</text>
      </svg>
    `.trim();
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>, post: BlogPost) => {
    const target = e.target as HTMLImageElement;
    target.src = getPlaceholderImage(post);
  };

  return (
    <>
      <Landing />
      
      {/* Featured Article - Exciting Hero Style */}
      {featuredBlogs.length > 0 && (
        <section className="relative py-16 md:py-24 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 dark:from-indigo-800 dark:via-purple-800 dark:to-pink-800 overflow-hidden">
          {/* Animated background elements */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob"></div>
            <div className="absolute top-40 right-10 w-72 h-72 bg-yellow-300 rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-2000"></div>
            <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-pink-300 rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-4000"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            {featuredBlogs.map((post) => (
              <div key={post.id} className="max-w-5xl mx-auto">
                {/* New Badge */}
                <div className="flex items-center justify-center mb-6">
                  <span className="inline-flex items-center px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-semibold">
                    <FaRocket className="mr-2" />
                    NEW ARTICLE
                  </span>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  {/* Content */}
                  <div className="text-white">
                    <h2 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                      {post.title}
                    </h2>
                    <p className="text-xl md:text-2xl text-white/90 mb-6 leading-relaxed">
                      {post.excerpt}
                    </p>
                    <div className="flex flex-wrap items-center gap-4 text-white/80 mb-6">
                      <span className="flex items-center text-sm">
                        <FaUser className="mr-2" />
                        {post.author}
                      </span>
                      <span className="flex items-center text-sm">
                        <FaCalendar className="mr-2" />
                        {new Date(post.publishedAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                      <span className="flex items-center text-sm">
                        <FaClock className="mr-2" />
                        {post.readTime} min read
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-6">
                      {post.tags.slice(0, 4).map((tag) => (
                        <span 
                          key={tag} 
                          className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <DynamicButton
                      variant="primary"
                      onClick={() => navigate(`/blog/${post.slug}`)}
                      className="bg-white text-indigo-600 hover:bg-gray-100 font-semibold px-8 py-3 text-lg"
                    >
                      Read Full Article
                      <FaArrowRight className="ml-2 inline" />
                    </DynamicButton>
                  </div>
                  
                  {/* Image */}
                  <div className="relative">
                    <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-300">
                      <img
                        src={post.imageUrl || getPlaceholderImage(post)}
                        alt={post.title}
                        className="w-full h-80 md:h-96 object-cover"
                        onError={(e) => handleImageError(e, post)}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Services Section */}
      {services.length > 0 && (
        <DynamicSection
          id="services"
          title="Web Development Services"
          subtitle="Expert solutions for your digital needs"
          className="bg-white dark:bg-gray-900"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon] || FaCode;
              return (
                <DynamicCard key={service.id} className="p-6 flex flex-col hover:shadow-xl transition-shadow">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 mb-4 flex-grow">
                    {service.description}
                  </p>
                  {service.features && service.features.length > 0 && (
                    <div className="mb-4">
                      <ul className="space-y-2">
                        {service.features.slice(0, 3).map((feature, index) => (
                          <li key={index} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <FaCheck className="mr-2 text-primary-600 dark:text-primary-400" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <DynamicButton
                    variant="outline"
                    onClick={() => navigate('/services')}
                    className="mt-auto"
                  >
                    Learn More
                    <FaArrowRight className="ml-2 inline" />
                  </DynamicButton>
                </DynamicCard>
              );
            })}
          </div>
        </DynamicSection>
      )}

      {/* Best Testimonials Section */}
      {testimonials.length > 0 && (
        <DynamicSection
          id="testimonials"
          title="What Clients Say"
          subtitle="Real feedback from web development projects"
          className="bg-gray-50 dark:bg-gray-800"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {testimonials.map((testimonial) => (
              <DynamicCard key={testimonial.id} className="p-6">
                <div className="mb-4">
                  <FaQuoteLeft className="text-4xl text-primary-600 dark:text-primary-400 mb-4" />
                  <p className="text-gray-700 dark:text-gray-300 italic mb-4 text-lg leading-relaxed">
                    "{testimonial.content}"
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                      {testimonial.imageUrl ? (
                        <img
                          src={testimonial.imageUrl}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <FaUser className="text-primary-600 dark:text-primary-400" />
                      )}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {testimonial.name}
                        {testimonial.countryCode && (
                          <span className="ml-2">{getCountryFlag(testimonial.countryCode)}</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {testimonial.role}
                        {testimonial.company && ` at ${testimonial.company}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <FaStar
                        key={i}
                        className={`w-5 h-5 ${
                          i < testimonial.rating
                            ? 'text-yellow-400'
                            : 'text-gray-300 dark:text-gray-600'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </DynamicCard>
            ))}
          </div>
          <div className="text-center mt-8">
            <DynamicButton
              variant="outline"
              onClick={() => navigate('/testimonials')}
            >
              View All Testimonials
              <FaArrowRight className="ml-2 inline" />
            </DynamicButton>
          </div>
        </DynamicSection>
      )}
    </>
  );
};

export default Home;
